import React, { forwardRef, ForwardRefRenderFunction, useState } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  FormLabelProps,
  Input,
  InputProps,
  Text,
} from '@chakra-ui/react';
import { PasswordInput } from '@olo-web/components/atoms/PasswordInput';
import { PhoneNumberInput } from '@olo-web/components/atoms/PhoneNumberInput/PhoneNumberInput.ui';
import { Control, Controller, FieldErrors, RegisterOptions, get } from 'react-hook-form';
import { onExpDateChange } from '@olo-web/utils/common/functions/onExpDateChange';

interface HookFormInputProps extends Omit<InputProps, 'onChange'> {
  errors?: FieldErrors;
  helperText?: string;
  control?: Control;
  rules?: RegisterOptions;
  label?: string;
  formState?: any;
  isRequired?: boolean;
  labelProps?: FormLabelProps;
  inputProps?: any;
  enabledSelectCountry?: boolean;
  isUsingYup?: boolean;
  nameReq?: string;
  labelTestId?: string;
}

const HookFormInputRenderer: ForwardRefRenderFunction<HTMLInputElement, HookFormInputProps> = (
  props,
  ref
) => {
  const {
    formState,
    errors,
    helperText,
    control,
    rules,
    type,
    name,
    label,
    children,
    isRequired = false,
    labelProps = {},
    inputProps = {},
    enabledSelectCountry = true,
    isUsingYup = false,
    nameReq,
    labelTestId,
    ...rest
  } = props;
  const [expDate, setExpDate] = useState('');
  const isPhone = name?.toLowerCase().includes('phone');
  const isExpirationDate = name?.toLowerCase().includes('expiration');
  const isPassword = type === 'password' || ['password', 'confirmPassword'].includes(name!);
  if (isPhone && !control) {
    console.error('"control" & "rules" props required with phone number input');
    return <Text color="danger.400">check the console</Text>;
  }

  return (
    <FormControl
      isInvalid={get(formState?.errors ?? errors, props?.name ?? '')}
      isRequired={!isUsingYup && isRequired}
    >
      {label && (
        <FormLabel {...labelProps} data-testid={labelTestId}>
          {label}
          {isUsingYup && isRequired && (
            <span
              role="presentation"
              aria-hidden="true"
              className="chakra-form__required-indicator css-19bcfdn"
            >
              *
            </span>
          )}
        </FormLabel>
      )}
      {isPassword ? (
        <PasswordInput
          aria-label={nameReq}
          name={name}
          ref={ref}
          type={type}
          borderColor="black"
          _hover={{ borderColor: 'black' }}
          _placeholder={{ color: 'blackAlpha.350' }}
          _focus={{
            borderColor: 'primary.400',
          }}
          {...rest}
        />
      ) : isPhone ? (
        <Controller
          control={control}
          name={name ?? ''}
          aria-label={nameReq}
          render={({ onChange, onBlur, value, name, ref: controllerRef }, { invalid }) => (
            <PhoneNumberInput
              aria-label={nameReq}
              value={value}
              name={name}
              country="US"
              onChange={onChange}
              onBlur={onBlur}
              ref={controllerRef}
              isInvalid={invalid}
              enabledSelectCountry={enabledSelectCountry}
              _placeholder={{ color: 'blackAlpha.350' }}
              borderColor="black"
              _hover={{ borderColor: 'black' }}
              _focus={{
                borderColor: 'primary.400',
              }}
              {...rest}
            />
          )}
        />
      ) : (
        <Input
          aria-label={nameReq}
          value={isExpirationDate ? expDate : undefined}
          _placeholder={{ color: 'blackAlpha.350' }}
          borderColor="black"
          _hover={{ borderColor: 'black' }}
          _focus={{
            borderColor: 'primary.400',
          }}
          // TODO: fix this we should probably handle the conditional logic inside the function
          onChange={
            isExpirationDate
              ? (e) => {
                  const value = onExpDateChange(e);
                  setExpDate(value);
                }
              : undefined
          }
          name={name}
          type={type}
          ref={ref}
          sx={{ caretColor: `${inputProps?.caretColor}` }}
          {...rest}
        />
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      <FormErrorMessage
        data-testid="email-or-name-input-error"
        role="alert"
        color="danger.400"
        mt="3px"
      >
        {get(formState?.errors ?? errors, name ?? '')?.message}
      </FormErrorMessage>
    </FormControl>
  );
};

export const HookFormInput = forwardRef(HookFormInputRenderer);
